import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { differenceInWeeks, startOfDay } from 'date-fns';

interface WeeklyPlan {
  id: string;
  main_priority: string;
  additional_tasks: string[];
  week_number: number;
}

function WeeklyPlanning() {
  const [currentSprint, setCurrentSprint] = useState<any>(null);
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyPlan>({
    id: '',
    main_priority: '',
    additional_tasks: [''],
    week_number: 1,
  });

  useEffect(() => {
    fetchCurrentSprint();
  }, []);

  const fetchCurrentSprint = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('User not authenticated');
        return;
      }

      const { data: sprint, error } = await supabase
        .from('sprints')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'in_progress')
        .maybeSingle();

      if (error) {
        console.error('Error fetching sprint:', error);
        return;
      }

      if (sprint) {
        setCurrentSprint(sprint);
        await fetchWeeklyPlan(sprint);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchWeeklyPlan = async (sprint: any) => {
    try {
      // Calculate the current week number
      const startDate = startOfDay(new Date(sprint.start_date));
      const today = startOfDay(new Date());
      const currentWeek = Math.max(1, differenceInWeeks(today, startDate) + 1);

      const { data, error } = await supabase
        .from('weekly_plans')
        .select('*')
        .eq('sprint_id', sprint.id)
        .eq('week_number', currentWeek)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching weekly plan:', error);
        return;
      }

      if (data) {
        setWeeklyPlan(data);
      } else {
        setWeeklyPlan({
          id: '',
          main_priority: '',
          additional_tasks: [''],
          week_number: currentWeek,
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddTask = () => {
    setWeeklyPlan({
      ...weeklyPlan,
      additional_tasks: [...weeklyPlan.additional_tasks, ''],
    });
  };

  const handleTaskChange = (index: number, value: string) => {
    const newTasks = [...weeklyPlan.additional_tasks];
    newTasks[index] = value;
    setWeeklyPlan({
      ...weeklyPlan,
      additional_tasks: newTasks,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error } = await supabase
        .from('weekly_plans')
        .upsert({
          sprint_id: currentSprint.id,
          week_number: weeklyPlan.week_number,
          main_priority: weeklyPlan.main_priority,
          additional_tasks: weeklyPlan.additional_tasks.filter(task => task.trim() !== ''),
        });

      if (error) {
        console.error('Error saving weekly plan:', error);
        alert('Failed to save weekly plan. Please try again.');
        return;
      }

      alert('Weekly plan saved successfully!');
    } catch (error) {
      console.error('Error:', error);
      alert('An unexpected error occurred. Please try again.');
    }
  };

  if (!currentSprint) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No active sprint found. Please create a new sprint first.</p>
        <a
          href="/create-sprint"
          className="inline-block mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
        >
          Create New Sprint
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Weekly Planning</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Main Priority for Week {weeklyPlan.week_number}
            <textarea
              value={weeklyPlan.main_priority}
              onChange={(e) => setWeeklyPlan({ ...weeklyPlan, main_priority: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              rows={3}
              required
            />
          </label>
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Additional Tasks</h2>
          {weeklyPlan.additional_tasks.map((task, index) => (
            <div key={index}>
              <input
                type="text"
                value={task}
                onChange={(e) => handleTaskChange(index, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter task"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddTask}
            className="text-blue-600 hover:text-blue-700"
          >
            + Add Another Task
          </button>
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Save Weekly Plan
        </button>
      </form>
    </div>
  );
}

export default WeeklyPlanning;